import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useNodesState,
} from "@xyflow/react";
import dagre from "dagre";
import "@xyflow/react/dist/style.css";

/**
 * Relationship graph for one AI asset: the asset at the centre, with the identities, models,
 * tools, data sources and repos it reaches.
 *
 * The backend already collapses parallel per-source assertions into a single edge and drops
 * self-loops (a self-loop means "facet of the asset", not a relationship), so this renders what
 * it is given rather than re-deriving either rule.
 */

const NODE_WIDTH = 190;
const NODE_HEIGHT = 62;

// Kind accents. The dot is the only hard-coded colour — it reads on both themes, while the card
// itself uses the theme tokens so it follows light/dark like the rest of the dashboard.
const KIND_STYLES = {
  Agent:           { label: "Agent",            dot: "bg-purple-600" },
  Application:     { label: "AI Application",   dot: "bg-purple-500" },
  ModelDeployment: { label: "Model Deployment", dot: "bg-purple-400" },
  McpServer:       { label: "MCP Server",       dot: "bg-indigo-500" },
  Workload:        { label: "Workload",         dot: "bg-slate-500" },
  // A person's own AI usage at a provider — not an agent, not an application.
  PersonalUse:     { label: "Personal use",     dot: "bg-pink-500" },
  Identity:        { label: "Identity",         dot: "bg-amber-500" },
  Model:           { label: "Model",            dot: "bg-blue-500" },
  Tool:            { label: "Tool",             dot: "bg-emerald-500" },
  DataSource:      { label: "Data Source",      dot: "bg-cyan-600" },
  Repo:            { label: "Repository",       dot: "bg-gray-500" },
  // What an identity is permitted to do — a granted role or app permission, not a thing it calls.
  PermissionTarget:{ label: "Permission",       dot: "bg-rose-600" },
};

/** Stable identity for a collapsed edge — the backend returns one row per (from, to, type). */
export const edgeKey = (e) => `${e.fromNodeId}-${e.toNodeId}-${e.edgeType}`;

/** Node kinds offered as filters, in the order they read as a hierarchy. */
export const FILTERABLE_KINDS = [
  "Agent", "Application", "ModelDeployment", "McpServer", "Workload", "PersonalUse",
  "Identity", "Model", "Tool", "DataSource", "Repo", "PermissionTarget",
];

export const KIND_LABEL = (kind) => (KIND_STYLES[kind] || {}).label || kind;

/** The accent for a node kind; an unknown kind falls back to the Workload style. */
export const styleForKind = (kind) => KIND_STYLES[kind] || KIND_STYLES.Workload;

/**
 * Applies the graph filters client-side. The payload is already capped by the server, so this
 * narrows what was fetched rather than re-querying.
 *
 * Dropping a node must also drop every edge touching it — React Flow throws on an edge whose
 * endpoint is missing, so a filter that only touched nodes would break the canvas rather than
 * simply hide things.
 */
export function filterGraph(graph, { kinds = [], providers = [], confidences = [] } = {}) {
  if (!graph) return graph;
  if (!kinds.length && !providers.length && !confidences.length) return graph;

  const keptNodes = (graph.nodes || []).filter(
    (n) =>
      (!kinds.length || kinds.includes(n.nodeKind)) &&
      (!providers.length || providers.includes(n.providerSystem)) &&
      (!confidences.length || confidences.includes(n.confidence))
  );
  const keptIds = new Set(keptNodes.map((n) => n.id));

  return {
    ...graph,
    nodes: keptNodes,
    edges: (graph.edges || []).filter(
      (e) => keptIds.has(e.fromNodeId) && keptIds.has(e.toNodeId)
    ),
  };
}

const EDGE_LABELS = {
  RunsAs: "runs as",
  UsesModel: "uses model",
  HasTool: "has tool",
  AccessesData: "accesses",
  DeployedFrom: "deployed from",
  GrantedPermission: "granted",
  InProject: "in project",
};

// Anything weaker than Corroborated is shown as a qualifier rather than stated as fact — the same
// "probable agent" honesty the confidence model exists for.
const SOFT_CONFIDENCE = new Set(["Probable", "Suspected", "Declared", "Unverified"]);

function GraphNode({ data }) {
  const style = styleForKind(data.nodeKind);
  const soft = SOFT_CONFIDENCE.has(data.confidence);

  return (
    <div
      className={`rounded-lg border bg-theme-card-solid px-3 py-2 shadow-sm cursor-pointer hover:border-border-hover ${
        data.isFocus ? "border-accent-purple" : "border-border"
      } ${
        // Selection is a ring rather than a border colour, so it stays visible on the focus node
        // (which is already purple-bordered) instead of being swallowed by it.
        data.isSelected ? "ring-2 ring-accent-purple" : ""
      }`}
      style={{ width: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Left} className="!bg-border" />
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
        <span className="text-[10px] font-medium uppercase tracking-wide text-content-secondary">
          {style.label}
        </span>
      </div>
      <div className="truncate text-sm font-semibold text-content-primary" title={data.label}>
        {data.label}
      </div>
      {soft && (
        <div className="text-[10px] text-content-muted">{data.confidence.toLowerCase()}</div>
      )}
      <Handle type="source" position={Position.Right} className="!bg-border" />
    </div>
  );
}

const nodeTypes = { graphNode: GraphNode };

/** Left-to-right dagre layout, so the graph reads as "asset → what it reaches". */
function layout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 24, ranksep: 90 });

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return nodes.map((n) => {
    const { x, y } = g.node(n.id);
    return { ...n, position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 } };
  });
}

/** Only the kinds actually present — a fixed ten-item key would be mostly noise. */
function Legend({ kinds }) {
  if (!kinds.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5">
      {kinds.map((kind) => {
        const style = styleForKind(kind);
        return (
          <span key={kind} className="flex items-center gap-1.5 text-[11px] text-content-secondary">
            <span className={`h-2 w-2 rounded-full ${style.dot}`} />
            {style.label}
          </span>
        );
      })}
    </div>
  );
}

export default function AiIntelligenceGraph({
  graph,
  focusNodeId,
  selectedNodeId,
  selectedEdgeKey,
  onNodeClick,
  onEdgeClick,
}) {
  // Positions are state, not derived: once the user drags a node we must not recompute them.
  // Layout is therefore keyed on graph *structure* only — selecting a node changes its styling
  // through a separate effect, so clicking never snaps the canvas back.
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [moved, setMoved] = useState(false);
  const flowRef = useRef(null);

  const structureKey = useMemo(() => {
    if (!graph?.nodes?.length) return "";
    return [
      graph.nodes.map((n) => n.id).join(","),
      (graph.edges || []).map(edgeKey).join(","),
    ].join("|");
  }, [graph]);

  const laidOutNodes = useMemo(() => {
    if (!graph?.nodes?.length) return [];

    const flowNodes = graph.nodes.map((n) => ({
      id: String(n.id),
      type: "graphNode",
      position: { x: 0, y: 0 },
      data: {
        label: n.name,
        nodeKind: n.nodeKind,
        confidence: n.confidence,
        isFocus: String(n.id) === String(focusNodeId),
        isSelected: String(n.id) === String(selectedNodeId),
      },
    }));
    const layoutEdges = (graph.edges || []).map((e) => ({
      source: String(e.fromNodeId),
      target: String(e.toNodeId),
    }));
    return layout(flowNodes, layoutEdges);
    // Deliberately keyed on structure: re-running on selection would discard the user's drags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey]);

  // Fresh layout whenever the graph's shape changes (new data, a filter, includeGone). Nodes
  // arrive after mount, so re-frame here rather than relying on the fitView prop, which fits at
  // init when there is nothing to fit.
  useEffect(() => {
    setNodes(laidOutNodes);
    setMoved(false); // a fresh layout is already the automatic one — nothing to reset back to
    if (laidOutNodes.length) {
      const id = requestAnimationFrame(() => flowRef.current?.fitView());
      return () => cancelAnimationFrame(id);
    }
  }, [laidOutNodes, setNodes]);

  // Restyle in place — position is preserved, so a drag survives selection changes.
  useEffect(() => {
    setNodes((current) =>
      current.map((n) => {
        const isFocus = n.id === String(focusNodeId);
        const isSelected = n.id === String(selectedNodeId);
        if (n.data.isFocus === isFocus && n.data.isSelected === isSelected) return n;
        return { ...n, data: { ...n.data, isFocus, isSelected } };
      })
    );
  }, [focusNodeId, selectedNodeId, setNodes]);

  const edges = useMemo(() => {
    if (!graph?.nodes?.length) return [];
    return (graph.edges || []).map((e) => {
      const key = edgeKey(e);
      const selected = key === selectedEdgeKey;
      return {
        id: key,
        source: String(e.fromNodeId),
        target: String(e.toNodeId),
        label: EDGE_LABELS[e.edgeType] || e.edgeType,
        labelStyle: { fontSize: 10, fill: "var(--text-secondary)" },
        labelBgStyle: { fill: "var(--bg-primary)" },
        style: {
          stroke: selected ? "var(--accent-purple)" : "var(--border-color)",
          // A corroborated edge (asserted by more than one source) is drawn heavier.
          strokeWidth: selected ? 3 : (e.sourceCount || 1) > 1 ? 2 : 1,
          strokeDasharray: SOFT_CONFIDENCE.has(e.confidence) ? "4 3" : undefined,
        },
        markerEnd: { type: MarkerType.ArrowClosed },
        data: e,
      };
    });
  }, [graph, selectedEdgeKey]);

  const kindsPresent = useMemo(
    () => [...new Set((graph?.nodes || []).map((n) => n.nodeKind))],
    [graph]
  );

  const handleNodeClick = useCallback(
    (_event, node) => onNodeClick?.(graph?.nodes?.find((n) => String(n.id) === node.id)),
    [graph, onNodeClick]
  );

  const handleEdgeClick = useCallback(
    (_event, edge) => onEdgeClick?.(edge.data),
    [onEdgeClick]
  );

  // Re-run dagre and re-frame. Restyling is reapplied by the effect above, so a reset does not
  // silently clear the current selection.
  const resetLayout = useCallback(() => {
    setNodes(laidOutNodes);
    flowRef.current?.fitView({ duration: 200 });
  }, [laidOutNodes, setNodes]);

  // Guards on the derived layout, not the node state — the state is populated by an effect, so
  // testing it would flash the empty message on every load.
  if (!laidOutNodes.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-content-secondary">
        No relationships discovered yet for this asset.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onNodeClick={handleNodeClick}
          onNodeDragStop={() => setMoved(true)}
          onEdgeClick={handleEdgeClick}
          onInit={(instance) => { flowRef.current = instance; }}
          fitView
          nodesConnectable={false}
        >
          <Background gap={16} color="var(--border-color)" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border">
        <Legend kinds={kindsPresent} />
        <button
          onClick={() => { resetLayout(); setMoved(false); }}
          disabled={!moved}
          className="mr-2 shrink-0 text-[11px] text-content-secondary hover:text-content-primary disabled:opacity-40 disabled:hover:text-content-secondary"
          title="Restore the automatic layout"
        >
          Reset layout
        </button>
      </div>
    </div>
  );
}
